import {User, Briefcase, TrendingUp, MessageCircle} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {Badge} from "@/components/ui/badge";
import {Progress} from "@/components/ui/progress";
import {getPsychotypeStyle, type AiPersona} from "@/lib/personas-api";

interface PersonaCardProps {
  persona: AiPersona;
  onClick?: () => void;
  onChatClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  // Edit mode prop - when true, card interactions are disabled
  isEditMode?: boolean;
}

export function PersonaCard({
  persona,
  onClick,
  onChatClick,
  className,
  style,
  isEditMode,
}: PersonaCardProps) {
  const confidencePercent = Math.round(persona.confidence_score * 100);
  const psychotypeStyle = getPsychotypeStyle(persona.psychotype);

  return (
    <div
      style={style}
      className={cn(
        "group relative flex h-full flex-col overflow-hidden rounded-xl border transition-all duration-300",
        "bg-card/60 backdrop-blur-sm",
        "border-border/40",
        // Only apply hover effects when not in edit mode
        !isEditMode &&
          "hover:border-border/60 overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/10",
        className
      )}
    >
      {/* Subtle gradient accent based on psychotype */}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 bg-linear-to-br to-transparent opacity-50 transition-opacity group-hover:opacity-100",
          psychotypeStyle.gradient
        )}
      />

      {/* Card content - clickable area */}
      <div
        className={cn("relative flex flex-1 flex-col p-5", !isEditMode && "cursor-pointer")}
        onClick={!isEditMode ? onClick : undefined}
      >
        {/* Header: Avatar + Name + Psychotype */}
        <div className="mb-4 flex items-start gap-3">
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-full",
              "bg-muted/50 ring-border/50 ring-1",
              "transition-all group-hover:ring-2",
              psychotypeStyle.ringHover
            )}
          >
            <User className="text-muted-foreground h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-semibold tracking-tight">{persona.name}</h3>
            <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5 text-sm">
              <Briefcase className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{persona.occupation}</span>
            </div>
          </div>

          <Badge
            variant="outline"
            className={cn("shrink-0 text-xs font-medium", psychotypeStyle.badge)}
          >
            {persona.psychotype}
          </Badge>
        </div>

        {/* Demographics row */}
        <div className="mb-4 flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {persona.age_range} · {persona.gender}
          </span>
        </div>

        {/* Interests */}
        <div className="mb-4">
          <p className="text-muted-foreground/70 mb-2 text-[11px] font-medium tracking-wider uppercase">
            {m.persona_card_interests()}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {persona.interests.slice(0, 3).map((interest) => (
              <Badge
                key={interest}
                variant="secondary"
                className="bg-muted/50 hover:bg-muted/70 text-xs font-normal"
              >
                {interest}
              </Badge>
            ))}
            {persona.interests.length > 3 && (
              <Badge
                variant="secondary"
                className="bg-muted/30 text-muted-foreground text-xs font-normal"
              >
                +{persona.interests.length - 3}
              </Badge>
            )}
          </div>
        </div>

        {/* Confidence Score */}
        <div className="mt-auto mb-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <TrendingUp className="h-3 w-3" />
              {m.persona_card_confidence()}
            </span>
            <span className="text-xs font-semibold tabular-nums">{confidencePercent}%</span>
          </div>
          <Progress value={confidencePercent} className="bg-muted/30 h-1.5" />
        </div>
      </div>

      {/* Chat CTA - always visible at bottom */}
      <div
        className={cn(
          "relative flex items-center justify-center px-5 pt-0 pb-5",
          "from-card/90 via-card/60 bg-linear-to-t to-transparent",
          "-mt-2"
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            if (isEditMode) return;
            e.stopPropagation();
            onChatClick?.();
          }}
          disabled={isEditMode}
          className={cn(
            "inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium",
            "bg-primary/90 text-primary-foreground",
            "shadow-primary/20 shadow-md",
            "transition-all duration-200",
            isEditMode ? "cursor-not-allowed opacity-60" : "hover:bg-primary hover:shadow-lg"
          )}
        >
          <MessageCircle className="h-4 w-4" />
          {m.persona_card_chat_with({name: persona.name.split(" ")[0]})}
        </button>
      </div>
    </div>
  );
}

// Loading skeleton for the card
export function PersonaCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={style}
      className={cn(
        "relative overflow-hidden rounded-xl border",
        "bg-card/40 backdrop-blur-sm",
        "border-border/30",
        className
      )}
    >
      <div className="p-5">
        {/* Header skeleton */}
        <div className="mb-4 flex items-start gap-3">
          <div className="bg-muted/40 h-11 w-11 shrink-0 animate-pulse rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="bg-muted/40 h-4 w-2/3 animate-pulse rounded" />
            <div className="bg-muted/30 h-3 w-1/2 animate-pulse rounded" />
          </div>
          <div className="bg-muted/30 h-5 w-16 animate-pulse rounded-full" />
        </div>

        {/* Demographics skeleton */}
        <div className="bg-muted/30 mb-4 h-4 w-24 animate-pulse rounded" />

        {/* Interests skeleton */}
        <div className="mb-4 space-y-2">
          <div className="bg-muted/20 h-3 w-16 animate-pulse rounded" />
          <div className="flex gap-1.5">
            <div className="bg-muted/30 h-5 w-14 animate-pulse rounded-full" />
            <div className="bg-muted/30 h-5 w-16 animate-pulse rounded-full" />
            <div className="bg-muted/30 h-5 w-12 animate-pulse rounded-full" />
          </div>
        </div>

        {/* Confidence skeleton */}
        <div className="mb-4 space-y-2">
          <div className="flex justify-between">
            <div className="bg-muted/20 h-3 w-20 animate-pulse rounded" />
            <div className="bg-muted/30 h-3 w-8 animate-pulse rounded" />
          </div>
          <div className="bg-muted/30 h-1.5 w-full animate-pulse rounded-full" />
        </div>
      </div>

      {/* Button skeleton */}
      <div className="bg-muted/40 mx-5 mb-5 h-10 animate-pulse rounded-lg" />
    </div>
  );
}
